import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { buildFileUrl } from "@/lib/api";
import { Play, Pause, SkipForward, SkipBack, X, ListMusic } from "lucide-react";

/**
 * Preview a playlist exactly the way a Fire Stick TV will render it.
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   playlist: { id, name, item_duration, media_ids, loop }
 *   media:    array of media docs (with id, media_type, filename)
 *   title:    optional override (e.g. TV name)
 */
export default function PlaylistPreviewDialog({ open, onClose, playlist, media, title }) {
  const items = useMemo(() => {
    if (!playlist || !media) return [];
    const map = new Map(media.map((m) => [m.id, m]));
    return (playlist.media_ids || []).map((id) => map.get(id)).filter(Boolean);
  }, [playlist, media]);

  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (open) {
      setIndex(0);
      setPlaying(true);
    }
  }, [open, playlist?.id]);

  const duration = (playlist?.item_duration || 8) * 1000;
  const current = items.length ? items[index % items.length] : null;
  const loopEnabled = playlist?.loop !== false;

  // Auto-advance for images while playing
  useEffect(() => {
    if (!open || !playing || !current) return;
    if (current.media_type !== "image") return;
    const t = setTimeout(() => {
      setIndex((i) => {
        const next = i + 1;
        if (next >= items.length) return loopEnabled ? 0 : i;
        return next;
      });
    }, duration);
    return () => clearTimeout(t);
  }, [open, playing, current, duration, items.length, loopEnabled]);

  const next = () =>
    setIndex((i) => {
      const n = i + 1;
      if (n >= items.length) return loopEnabled ? 0 : i;
      return n;
    });
  const prev = () => setIndex((i) => (i - 1 + items.length) % items.length);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl p-0 overflow-hidden bg-black border-0"
        data-testid="playlist-preview-dialog"
      >
        <DialogTitle className="sr-only">{title || playlist?.name || "Playlist preview"}</DialogTitle>
        <DialogDescription className="sr-only">
          Live preview of the playlist as it will appear on the television.
        </DialogDescription>
        <div className="relative aspect-video bg-black w-full">
          {!current ? (
            <div className="absolute inset-0 grid place-items-center text-white/60 text-sm">
              {playlist ? "Playlist is empty" : "No playlist"}
            </div>
          ) : current.media_type === "image" ? (
            <img
              key={current.id}
              src={buildFileUrl(current.id, "admin")}
              alt=""
              className="absolute inset-0 w-full h-full object-contain"
            />
          ) : (
            <video
              key={current.id}
              src={buildFileUrl(current.id, "admin")}
              autoPlay={playing}
              muted
              playsInline
              onEnded={next}
              className="absolute inset-0 w-full h-full object-contain"
            />
          )}

          {/* Header */}
          <div className="absolute top-0 inset-x-0 px-4 py-3 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
            <div className="flex items-center gap-2 text-white/90 text-xs uppercase tracking-[0.25em]">
              <ListMusic className="h-4 w-4" strokeWidth={1.25} />
              {title || playlist?.name || "Preview"}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/10"
              data-testid="playlist-preview-close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Controls */}
          <div className="absolute bottom-0 inset-x-0 px-4 py-3 bg-gradient-to-t from-black/70 to-transparent">
            <div className="flex items-center justify-between text-white">
              <div className="text-xs text-white/70 truncate">
                {current ? `${(index % items.length) + 1} / ${items.length} · ${current.filename}` : ""}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={prev}
                  disabled={!items.length}
                  className="text-white hover:bg-white/10"
                  data-testid="playlist-preview-prev"
                >
                  <SkipBack className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPlaying((p) => !p)}
                  disabled={!items.length}
                  className="text-white hover:bg-white/10"
                  data-testid="playlist-preview-toggle"
                >
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={next}
                  disabled={!items.length}
                  className="text-white hover:bg-white/10"
                  data-testid="playlist-preview-next"
                >
                  <SkipForward className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Progress strip */}
            <div className="mt-2 flex gap-1">
              {items.map((m, i) => (
                <div
                  key={m.id}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    i === index % items.length ? "bg-white" : "bg-white/25"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
